import { formatMark, type EventDef } from '@decathlon/shared';
import { clear, EVENT_EMOJI, h } from '../dom.js';
import { affiliatePanel } from './affiliate.js';

export interface EventResult {
  ev: EventDef;
  mark: number | null;
  points: number;
  pb: number | undefined;
  isPb: boolean;
  standard: number;
  passed: boolean;
  details: { label: string; value: string }[];
  tips: string[];
}

export interface ResultActions {
  again(): void;
  menu(): void;
  stats(): void;
}

export function renderResult(root: HTMLElement, r: EventResult, a: ResultActions): void {
  clear(root);
  const track = r.ev.unit === 'seconds';
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel results stack' },
        h('h1', {}, `${EVENT_EMOJI[r.ev.id] ?? '🏅'} ${r.ev.name}`),
        h(
          'div',
          { class: 'row big-result' },
          h(
            'div',
            { class: 'stat huge' },
            h('div', { class: 'v mono' }, formatMark(r.ev, r.mark)),
            h('div', { class: 'l' }, track ? 'Your time' : 'Your best'),
          ),
          h(
            'div',
            { class: 'stat huge' },
            h('div', { class: 'v mono' }, String(r.points)),
            h('div', { class: 'l' }, 'IAAF points'),
          ),
          h(
            'div',
            { class: `stat huge ${r.passed ? 'pass' : 'fail'}` },
            h('div', { class: 'v' }, r.passed ? '✔' : '✘'),
            h('div', { class: 'l' }, `Standard ${formatMark(r.ev, r.standard)}`),
          ),
        ),
        r.isPb
          ? h('div', { class: 'toast-inline ok' }, '🏆 New personal best!')
          : r.pb !== undefined
            ? h('p', { class: 'muted' }, `Personal best: ${formatMark(r.ev, r.pb)}`)
            : null,
        h(
          'table',
          {},
          h(
            'tbody',
            {},
            r.details.map((d) =>
              h('tr', {}, h('td', {}, d.label), h('td', { class: 'num' }, d.value)),
            ),
            h(
              'tr',
              {},
              h('td', {}, 'World class'),
              h('td', { class: 'num' }, formatMark(r.ev, r.ev.worldClass)),
            ),
          ),
        ),
        h(
          'div',
          { class: 'stack' },
          r.tips.map((t) => h('div', { class: 'hint' }, h('b', {}, '🧠 Coach'), t)),
        ),
        h(
          'div',
          { class: 'row' },
          h('button', { class: 'btn primary', onclick: () => a.again() }, '↻ Again (Enter)'),
          h('button', { class: 'btn blue', onclick: () => a.stats() }, '📈 Progress'),
          h('button', { class: 'btn ghost', onclick: () => a.menu() }, '← Menu (Esc)'),
        ),
        affiliatePanel(),
      ),
    ),
  );
}

export interface MeetIntroModel {
  ev: EventDef;
  index: number;
  total: number;
  standard: number;
  pb: number | undefined;
  leftKey: string;
  rightKey: string;
  /** Server time the event begins. */
  eventAt: number;
  serverNow(): number;
  turnBased: boolean;
  introMs: number;
}

/** The card shown between events in a meet, with a countdown to the start. */
export function renderMeetIntro(root: HTMLElement, m: MeetIntroModel): void {
  clear(root);
  const bar = h('div', { class: 'intro-bar-fill' });
  const count = h('span', { class: 'mono' });
  const tick = () => {
    const left = Math.max(0, m.eventAt - m.serverNow());
    bar.style.width = `${Math.min(100, (1 - left / m.introMs) * 100)}%`;
    count.textContent = `${Math.ceil(left / 1000)} s`;
    if (left > 0 && count.isConnected) setTimeout(tick, 100);
  };
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel intro stack' },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          h('h1', {}, `${EVENT_EMOJI[m.ev.id] ?? '🏅'} ${m.ev.name}`),
          h('span', { class: 'badge' }, `Event ${m.index + 1} of ${m.total}`),
        ),
        h('p', { class: 'lead' }, m.ev.blurb),
        h(
          'div',
          { class: 'row' },
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, formatMark(m.ev, m.standard)),
            h('div', { class: 'l' }, 'Qualifying standard'),
          ),
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, m.pb !== undefined ? formatMark(m.ev, m.pb) : '—'),
            h('div', { class: 'l' }, 'Your PB'),
          ),
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, formatMark(m.ev, m.ev.worldClass)),
            h('div', { class: 'l' }, 'World class'),
          ),
        ),
        h(
          'div',
          { class: 'keys-panel static' },
          h('kbd', { class: 'runkey' }, m.leftKey),
          h('div', { class: 'key-hint' }, 'alternate'),
          h('kbd', { class: 'runkey' }, m.rightKey),
          m.turnBased || m.ev.id === '110h' ? h('kbd', { class: 'runkey space' }, 'Space') : null,
        ),
        h(
          'p',
          { class: 'muted small' },
          m.turnBased
            ? m.ev.id === 'pv'
              ? 'Your turn: alternate the run keys, tap Space in the plant zone, then tap again when the release indicator is green. 30 seconds to start.'
              : 'Your turn: build speed or power with the two keys, hold Space for the angle gauge, then release. Watch the take-off line and sector indicator. 30 seconds to start.'
            : 'Everyone starts together. Wait for the gun: pressing early costs a 0.6 s penalty.',
        ),
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          h('span', { class: 'muted' }, 'Starting in ', count),
          h('div', { class: 'intro-bar' }, bar),
        ),
      ),
    ),
  );
  tick();
}

export interface IntroModel {
  ev: EventDef;
  standard: number;
  pb: number | undefined;
  leftKey: string;
  rightKey: string;
  /** High jump: pick the starting bar height. */
  heights?: { options: number[]; value: number };
}

export function renderIntro(
  root: HTMLElement,
  m: IntroModel,
  go: (choice: number | undefined) => void,
  back: () => void,
): void {
  clear(root);
  let choice = m.heights?.value;
  const jump = m.ev.kind === 'jump' || m.ev.kind === 'vault' || m.ev.kind === 'throw';
  const heightBtns = m.heights
    ? h(
        'div',
        { class: 'toggle' },
        m.heights.options.map((o) => {
          const b = h('button', { class: o === choice ? 'on' : '' }, `${o.toFixed(2)} m`);
          b.addEventListener('click', () => {
            choice = o;
            for (const c of heightBtns!.children) c.classList.toggle('on', c === b);
          });
          return b;
        }),
      )
    : null;
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel intro stack' },
        h('h1', {}, `${EVENT_EMOJI[m.ev.id] ?? '🏅'} ${m.ev.name}`),
        h('p', { class: 'lead' }, m.ev.blurb),
        h(
          'div',
          { class: 'row' },
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, formatMark(m.ev, m.standard)),
            h('div', { class: 'l' }, 'Qualifying standard'),
          ),
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, m.pb !== undefined ? formatMark(m.ev, m.pb) : '—'),
            h('div', { class: 'l' }, 'Your PB'),
          ),
          h(
            'div',
            { class: 'stat' },
            h('div', { class: 'v mono' }, formatMark(m.ev, m.ev.worldClass)),
            h('div', { class: 'l' }, 'World class'),
          ),
        ),
        heightBtns ? h('div', {}, h('label', {}, 'Starting height'), heightBtns) : null,
        h(
          'div',
          { class: 'keys-panel static' },
          h('kbd', { class: 'runkey' }, m.leftKey),
          h('div', { class: 'key-hint' }, 'alternate'),
          h('kbd', { class: 'runkey' }, m.rightKey),
          jump ? h('kbd', { class: 'runkey space' }, 'Space') : null,
        ),
        h(
          'p',
          { class: 'muted small' },
          m.ev.kind === 'sprint'
            ? 'Wait for the gun. Pressing early is a false start and the race restarts.'
            : 'Run up with the two keys. Press Space where you want to take off, hold it to raise the angle gauge, release to fly.',
        ),
        h(
          'div',
          { class: 'row' },
          h('button', { class: 'btn primary', onclick: () => go(choice) }, '🏁 Go (Enter)'),
          h('button', { class: 'btn ghost', onclick: back }, '← Back'),
        ),
      ),
    ),
  );
}
